import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  GET_COMMUNITY_TEMPLATES,
  GET_CONSULT_NOTE_TEMPLATES,
  GET_DOCUMENT_TEMPLATES,
  GET_ORGANIZATION_TEMPLATES,
  GET_PINNED_TEMPLATES,
  GET_TEMPLATES,
  type TemplateResponseV2 as Template,
} from '../../contracts';
import { deleteTemplateV2 } from '../api/templates';

// The three lists a deleted template can appear in. The legacy hook spelled
// each cancel/setQueryData pair out longhand; the loop is the same three keys
// in the same order, so the cancel sequence and the write order are unchanged.
const OPTIMISTIC_TEMPLATE_QUERY_KEYS = [
  GET_TEMPLATES,
  GET_DOCUMENT_TEMPLATES,
  GET_CONSULT_NOTE_TEMPLATES,
] as const;

type UseMutateDeleteTemplateProps = {
  /**
   * Crosses as a PARAMETER, not an import (cohort-C).
   *
   * Its source, `useCombinedOrganizationInfo`, STAYS APP-SIDE BY RATIFIED
   * DISPOSITION (workspace OT3). The S5 cohort files this hook under "waits
   * for OT3 to land `useCombinedOrganizationInfo`" — an unblock event that
   * cannot occur, because that hook is never landing in a package. Nothing was
   * blocking this carve; the boolean just had to cross as a value.
   */
  isOrganizationTemplateManager: boolean;
  /** Fires in `onMutate`, before the request — where the legacy dispatch was. */
  onTemplateDeleted?: (templateId: string) => void;
  /**
   * Fires in `onSuccess`, and ONLY when `isOrganizationTemplateManager` — the
   * legacy hook nested this dispatch inside that branch alongside the
   * organization-list invalidation, so the port is nested identically.
   */
  onGlobalTemplateDeleted?: (templateId: string) => void;
  /** Fires in `onSuccess`, where the success toast was. */
  onDeleted?: () => void;
  /**
   * Fires in `onError` — and only for an `AxiosError`, matching the legacy
   * hook, which showed nothing at all for a non-Axios failure.
   */
  onDeleteFailed?: (description: string | undefined) => void;
};

/**
 * Deletes one template, optimistically removing it from the three lists it can
 * appear in and reconciling the rest by invalidation.
 *
 * Headless: both toasts and both analytics events are app reactions, exposed
 * here as ports fired at the positions their legacy statements occupied.
 */
export function useMutateDeleteTemplate({
  isOrganizationTemplateManager,
  onTemplateDeleted,
  onGlobalTemplateDeleted,
  onDeleted,
  onDeleteFailed,
}: UseMutateDeleteTemplateProps) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => deleteTemplateV2(templateId),

    onMutate: async (templateId: string) => {
      await queryClient.cancelQueries({ queryKey: [GET_TEMPLATES] });
      onTemplateDeleted?.(templateId);

      // The legacy hook snapshotted only `GET_TEMPLATES` — and then never used
      // it. Since the snapshot is now actually restored on failure (ARCFE-1031),
      // it has to cover every list the removal touches, or a failed delete
      // would half-restore: visible in one list, still missing from two.
      const previousTemplates = OPTIMISTIC_TEMPLATE_QUERY_KEYS.map(
        (key) => [key, queryClient.getQueryData<Template[]>([key])] as const
      );

      for (const key of OPTIMISTIC_TEMPLATE_QUERY_KEYS) {
        // `GET_TEMPLATES` was already cancelled above, before the analytics
        // call; cancelling it a second time is a no-op, and keeping it in the
        // loop is what makes the three writes provably identical.
        await queryClient.cancelQueries({ queryKey: [key] });
        queryClient.setQueryData<Template[]>([key], (oldTemplates) =>
          oldTemplates?.filter((template) => template.id !== templateId)
        );
      }

      return { previousTemplates };
    },

    onSuccess: async (_, templateId) => {
      await queryClient.invalidateQueries({ queryKey: [GET_COMMUNITY_TEMPLATES] });
      await queryClient.invalidateQueries({ queryKey: [GET_PINNED_TEMPLATES] });
      if (isOrganizationTemplateManager) {
        await queryClient.invalidateQueries({ queryKey: [GET_ORGANIZATION_TEMPLATES] });
        onGlobalTemplateDeleted?.(templateId);
      }
      onDeleted?.();
    },

    onError: (error, _templateId, context) => {
      // FIX (ARCFE-1031, from review of #18346): the legacy hook captured
      // `previousTemplates` in `onMutate` and then never used it, so a failed
      // delete left the row hidden from all three lists until some unrelated
      // refetch brought it back. The snapshot is now actually restored — the
      // same rollback `useMutateBulkDeleteTemplates` already performs.
      context?.previousTemplates.forEach(([key, snapshot]) => {
        if (snapshot) queryClient.setQueryData([key], snapshot);
      });

      if (error instanceof AxiosError) {
        // The legacy hook read `errorData.detail.msg` off an untyped body and
        // would have thrown on an error response without a `detail`. The
        // optional chain is the only behaviour change: a malformed body now
        // surfaces the toast with no description instead of throwing inside
        // `onError`.
        const description = (
          error.response?.data as { detail?: { msg?: string } } | undefined
        )?.detail?.msg;
        onDeleteFailed?.(description);
      }
    },
  });
}
